import React from "react";
import { useTranslation } from "react-i18next";
import { AppSettings, ExtensionsStatusData, ExtensionType, SensitivityLabelMode } from "../../types";

interface SensitivityLabelsSectionProps {
  settings: AppSettings | null;
  extensionsStatus: ExtensionsStatusData | null;
  saving: boolean;
  verifyingExtension: ExtensionType | null;
  onActivateExtension: (extensionKey: ExtensionType) => void;
  onVerifyExtension: (extensionKey: ExtensionType) => void;
  onChangeSensitivityLabelMode: (mode: SensitivityLabelMode) => void;
}

export const SensitivityLabelsSection: React.FC<SensitivityLabelsSectionProps> = ({
  settings,
  extensionsStatus,
  saving,
  verifyingExtension,
  onActivateExtension,
  onVerifyExtension,
  onChangeSensitivityLabelMode,
}) => {
  const { t } = useTranslation('adminPage');

  const featureDetail = extensionsStatus?.featureDetails?.find(
    (f) => f.key === 'sensitivity_labels'
  );

  const isPremium = featureDetail?.isPremium ?? true;
  const hasPaidAccess = !isPremium || Boolean(extensionsStatus?.isPaidTier);
  const sensitivityExtension = extensionsStatus?.extensions?.sensitivity;
  const isConsented = Boolean(sensitivityExtension?.consented);

  return (
    <div className="setup-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <h2>{t('sections.sensitivityLabels.title', '🏷️ Étiquettes de sensibilité Purview')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {isPremium && (
            <span className="admin-premium-badge">
              {t('sections.sensitivityExtension.premiumBadge', '⭐ Fonctionnalité Premium')}
            </span>
          )}
          {hasPaidAccess && (
            isConsented ? (
              <span className="admin-extension-badge">
                {t('sections.sensitivityExtension.extensionActive', '✓ Extension Sensibilité active')}
              </span>
            ) : (
              <span className="admin-premium-badge">
                ⚠️ {t('sections.sensitivityExtension.warningTitle', 'Extension Sensibilité requise')}
              </span>
            )
          )}
        </div>
      </div>
      <p className="help-text">
        {t('sections.sensitivityLabels.description', 'Permet d\'attribuer et d\'appliquer des étiquettes de sensibilité Microsoft Purview aux équipes créées.')}
      </p>

      {/* Bannière d'avertissement Free Plan ou Extension Sensitivity manquante */}
      {!hasPaidAccess ? (
        <div className="admin-extension-banner premium admin-mb-16">
          <div className="admin-extension-banner-header">
            <strong>{t('sections.sensitivityExtension.premiumBadge', '⭐ Fonctionnalité Premium')}</strong>
          </div>
          <p className="admin-extension-banner-desc">
            {t('sections.sensitivityExtension.premiumDescription', 'La classification et l\'application des étiquettes de sensibilité Purview sont réservées aux organisations disposant d\'un abonnement payant actif.')}
          </p>
          <div className="admin-extension-actions">
            <button
              className="btn btn-primary btn-small"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {t('sections.securityGroup.upgradeButton', 'Voir les abonnements')}
            </button>
          </div>
        </div>
      ) : !isConsented ? (
        <div className="admin-extension-banner warning admin-mb-16">
          <div className="admin-extension-banner-header">
            <strong>⚠️ {t('sections.sensitivityExtension.warningTitle', 'Extension Sensibilité requise')}</strong>
          </div>
          <p className="admin-extension-banner-desc">
            {t('sections.sensitivityExtension.warningDescription', 'L\'application modulaire « RequestATeam - Extension Sensitivity Labels Purview » (InformationProtectionPolicy.Read) n\'est pas encore consentie. Les étiquettes ne pourront pas être lues ni appliquées tant que l\'extension n\'est pas activée.')}
          </p>
          <div className="admin-extension-actions">
            <button
              className="btn btn-primary btn-small"
              onClick={() => onActivateExtension('sensitivity')}
            >
              {t('sections.sensitivityExtension.activateExtension', 'Accorder le consentement administrateur')}
            </button>
            <button
              className="btn btn-secondary btn-small"
              onClick={() => onVerifyExtension('sensitivity')}
              disabled={verifyingExtension === 'sensitivity'}
            >
              {verifyingExtension === 'sensitivity' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span className="admin-spinner-inline" />
                  {t('sections.sensitivityExtension.verifying', 'Vérification en cours...')}
                </span>
              ) : (
                t('sections.sensitivityExtension.verifyExtension', 'Vérifier l\'activation')
              )}
            </button>
          </div>
        </div>
      ) : null}

      {/* Choix du mode de sensibilité */}
      <div className="status-card admin-mb-16">
        <div className="status-content">
          <label className="admin-approval-mode-label">
            {t('sections.sensitivityLabels.modeLabel', 'Politique d\'étiquetage')}
          </label>
          <div className={`admin-approval-options-container${saving ? ' saving' : ''}`}>
            {(['Disabled', 'Optional', 'Mandatory'] as SensitivityLabelMode[]).map((mode) => {
              const sensitivityExtensionActive = hasPaidAccess && isConsented;
              const effectiveMode = sensitivityExtensionActive ? (settings?.sensitivityLabelMode ?? 'Disabled') : 'Disabled';
              const isOptionDisabled = mode === 'Disabled' ? saving : (saving || !sensitivityExtensionActive);
              return (
                <label
                  key={mode}
                  className={`admin-radio-option${effectiveMode === mode ? ' selected' : ''}${isOptionDisabled ? ' disabled' : ''}`}
                >
                  <input
                    type="radio"
                    name="sensitivityLabelMode"
                    value={mode}
                    checked={effectiveMode === mode}
                    onChange={() => onChangeSensitivityLabelMode(mode)}
                    disabled={isOptionDisabled}
                    className="admin-radio-input"
                  />
                  <div className="admin-radio-content">
                    <div className="admin-radio-title">
                      {t(`sections.sensitivityLabels.modes.${mode}.title`, mode)}
                    </div>
                    <div className="admin-radio-desc">
                      {t(`sections.sensitivityLabels.modes.${mode}.description`, '')}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
